// frontend/src/widgets/QuoteNoteSnippetsWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd
// Author:       Castle Traffic Ltd
// Created:      2026-10-27
// Updated:      2026-10-27 GMT
// Description: Tenant-safe CRUD manager for reusable Specific Notes HTML snippets.
// Host: Renders CONTENT ONLY; dashboard supplies WidgetWrapper/title/close.
// Access: API enforces management permissions, UI cannot bypass them.
import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import TiptapEmailEditor from "../components/TiptapEmailEditor.jsx";

const ENDPOINT = "/api/quote-note-snippets";
const empty = { title: "", body_html: "<p></p>", sort_order: 0, is_active: true };
const explain = (err) => err?.response?.data?.detail || err?.message || "Operation failed";

export default function QuoteNoteSnippetsWidget() {
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({ ...empty });
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(ENDPOINT, { params: { include_inactive: true } });
      setRecords(Array.isArray(data) ? data : []);
      setError("");
    } catch (err) { setError(explain(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const reset = () => { setEditingId(null); setForm({ ...empty }); };
  const save = async () => {
    setError(""); setMessage(""); setBusy(true);
    try {
      if (!form.title.trim() || !form.body_html.replace(/<[^>]*>/g, "").trim()) {
        throw new Error("A title and nonempty note body are required");
      }
      const payload = { ...form, title: form.title.trim(), sort_order: Number(form.sort_order) || 0 };
      if (editingId != null) await axiosInstance.patch(`${ENDPOINT}/${editingId}`, payload);
      else await axiosInstance.post(ENDPOINT, payload);
      reset(); await load(); setMessage("Saved");
    } catch (err) { setError(explain(err)); }
    finally { setBusy(false); }
  };
  const remove = async (record) => {
    if (!window.confirm(`Delete reusable note “${record.title}”? Existing quotes will not change.`)) return;
    setBusy(true); setError(""); setMessage("");
    try { await axiosInstance.delete(`${ENDPOINT}/${record.id}`); if (editingId === record.id) reset(); await load(); }
    catch (err) { setError(explain(err)); }
    finally { setBusy(false); }
  };
  const choose = (record) => { setEditingId(record.id); setForm({ title: record.title, body_html: record.body_html, sort_order: record.sort_order, is_active: record.is_active }); };
  return (
    <div className="min-w-0 space-y-3 p-3 text-gray-800" onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
      {error && <div role="alert" className="rounded bg-red-50 p-2 text-sm text-red-800">{error}</div>}
      {message && <div role="status" className="rounded bg-green-50 p-2 text-sm">{message}</div>}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(220px,1fr)_minmax(0,2fr)]">
        <section className="min-w-0 space-y-2">
          <div className="flex items-center justify-between"><strong>Saved notes</strong><button className="rounded border px-3 py-1 text-sm" onClick={reset}>New</button></div>
          <div className="max-h-96 space-y-2 overflow-y-auto">
            {records.map((r) => <div key={r.id} className="rounded border p-2 text-sm">
              <div className="flex justify-between gap-2"><strong className="break-words">{r.title}</strong><span>{r.is_active ? "Active" : "Inactive"}</span></div>
              <div className="mt-2 flex gap-2"><button className="rounded border px-2 py-1" onClick={() => choose(r)}>Edit</button><button disabled={busy} className="rounded border px-2 py-1" onClick={() => remove(r)}>Delete</button></div>
            </div>)}
            {!records.length && <p className="text-sm text-gray-500">No reusable notes configured.</p>}
          </div>
        </section>
        <section className="min-w-0 space-y-3">
          <strong>{editingId == null ? "Create reusable note" : "Edit reusable note"}</strong>
          <label className="block text-sm">Title<input className="mt-1 w-full rounded border p-2" maxLength={200} value={form.title} onChange={(e) => setForm((f) => ({...f,title:e.target.value}))}/></label>
          <label className="block text-sm">Display order<input type="number" min="0" className="mt-1 w-28 rounded border p-2" value={form.sort_order} onChange={(e) => setForm((f) => ({...f,sort_order:e.target.value}))}/></label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({...f,is_active:e.target.checked}))}/>Available for insertion</label>
          <div className="text-sm font-medium">Formatted content</div>
          <div className="min-w-0 rounded border p-2"><TiptapEmailEditor value={form.body_html} onChange={(v) => setForm((f) => ({...f,body_html:v}))}/></div>
          <div className="flex gap-2"><button disabled={busy} className="rounded bg-gray-800 px-4 py-2 text-white disabled:opacity-50" onClick={save}>{busy ? "Saving…" : "Save note"}</button><button className="rounded border px-4 py-2" onClick={reset}>Cancel</button></div>
        </section>
      </div>
    </div>
  );
}
